import { observer } from 'mobx-react-lite'
import { FC, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { undoManager } from '../../../stores/StudioStore'
import { Box, Column } from '../../core/Components'
import { blue5 } from '../../platform/color'
import { StudioDraggableTypes } from '../StudioUtils'
import useTrackDrop from './useTrackDrop'

const Container = styled(Column, {
  width: '100%',
  height: '14px',
  marginTop: '-7px',
  position: 'absolute',
  zIndex: 10,
  opacity: '30%',
  transition: '100ms linear'
})

interface BetweenTrackDropzoneProps {
  stemIndex: number
  forceGlow?: boolean
}

const BetweenTracksDropzone: FC<BetweenTrackDropzoneProps> = observer(
  ({ stemIndex, forceGlow }) => {
    const { studio } = useTreeState()
    const dropzone = useRef<HTMLDivElement>()

    // for reordering instrument tracks
    const { isTrackOver, connectTrackDrop } = useTrackDrop(stemIndex)

    // For dropping a new sample or stem.
    const [{ isOver }, connectSoundDrop] = useDrop(
      () => ({
        accept: [StudioDraggableTypes.SAMPLE, StudioDraggableTypes.STEM],
        drop: (item: any) => {
          undoManager.startGroup(() => {
            if (item.type === StudioDraggableTypes.STEM) {
              studio.addStemFromRemix(item.stem)
            } else if (item.type === StudioDraggableTypes.SAMPLE) {
              studio.addNewStemForSample(item.sample)
            }
            studio.song.changeStemIndex(studio.song.stems.length - 1, stemIndex)
            studio.setSelectedStemIndex(stemIndex)
            undoManager.stopGroup()
          })
        },
        collect: (monitor) => ({
          isOver: !!monitor.isOver()
        })
      }),
      [stemIndex]
    )

    connectTrackDrop(dropzone)
    connectSoundDrop(dropzone)
    const showGradient = isOver || isTrackOver || forceGlow
    return (
      <Container ref={dropzone} verticalCenter>
        <Box
          css={{
            height: '8px',
            backgroundImage: showGradient && `linear-gradient(transparent, ${blue5}, transparent)`
          }}
        />
      </Container>
    )
  }
)

export default BetweenTracksDropzone
